import {customElement, property, query, state} from "lit/decorators.js";
import {css, html, LitElement, nothing, PropertyValues, unsafeCSS} from "lit";
import pdqDataBroker from "../services/PdqDataBroker";

import '@finos/perspective-viewer/dist/esm/perspective-viewer.inline.js'
import '@finos/perspective-viewer-datagrid/dist/esm/perspective-viewer-datagrid.js'

import '@carbon/web-components/es/components/copy-button/index.js';
import '@carbon/web-components/es/components/icon-button/index.js';
import '@carbon/web-components/es/components/textarea/index.js';
import Icon from '@carbon/web-components/es/icons/debug/16.js';
import Save from '@carbon/web-components/es/icons/save/16.js';
import Upload from '@carbon/web-components/es/icons/upload/16.js';
import Download from '@carbon/web-components/es/icons/download/16.js';

import Debug from "debug";
import {ReplaySubject} from "../observable/ReplaySubject";
import {Table, View} from "@finos/perspective";
import {col, grow, hide, margin, padding, row} from "../styles";
import pdqHotkeyService from "../services/PdqHotkeyService";
import {PdqDiscoverable, pdqDiscoveryLayer} from "./_pdq-mixin";
import {TAG_TYPE} from "@carbon/web-components/es/components/tag/defs";
import {PerspectiveViewerElement} from "@finos/perspective-viewer/dist/esm/perspective";
import "@finos/perspective-viewer/dist/esm/perspective-viewer.inline";

import staticStyles from 'bundle-text:@finos/perspective-viewer/dist/css/themes.css';
import {defer, extractTextAndScripts} from "../utils";
import {notificationService} from "../services/notificationService";

const PRE_INIT = Symbol('PRE-INIT')
const log = Debug('pdq:flow:perspective-viewer');
@customElement('pdq-perspective-viewer')
@pdqDiscoveryLayer(log, Icon, 'pdq-perspective-viewer', 'rgba(83, 83, 236, 0.13)', TAG_TYPE.PURPLE)
export class PdqPerspectiveViewer extends LitElement implements PdqDiscoverable {
  static styles = [hide, row, col, grow, padding, margin, unsafeCSS(staticStyles)]
  private _loaded = {...defer(), complete: false};
  private _perspectiveAvailiable = defer();
  private selectedView: ReplaySubject<View>|undefined;
  @property() get loaded(){
    return this._loaded.promise;
  }
  @property() show: boolean = false;
  @property() height: string = '500px';
  @property() output: Table|undefined;

  @state() dataPreview = 'no data';

  #view: string | undefined = undefined;
  #table: string | undefined = undefined;

  @property() error: string | null = null;
  @property({type: String})
  get table() {
    return this.#table || '';
  }
  set table(value: string) {
    this.#table = value;
    this.updateData()
  }
  @property({type: Boolean}) selectable : boolean = false

  @state()
  config: string|Symbol = PRE_INIT

  @query('perspective-viewer')
  perspectiveViewer: PerspectiveViewerElement|undefined;
  @query('cds-textarea')
  configEditor: HTMLTextAreaElement|undefined;

  private dataSource: ReplaySubject<Table>|null = null;
  static next_id = 0
  _id = PdqPerspectiveViewer.next_id++
  constructor() {
    super();
    this.id = this.id || `pdq-perspective-viewer-${this._id}`
  }

  protected firstUpdated(props: PropertyValues) {
    if (this.perspectiveViewer) {
      log('adding listeners')
      this.perspectiveViewer?.addEventListener('perspective-config-change', (evt: any) => {
        log('perspective-config-change', evt)
        this._updatePreview()
      })
      this.perspectiveViewer?.addEventListener('perspective-click', async (evt: any) => {
        log('perspective-click', evt)
        const meta = await pdqDataBroker.getTableMeta(this.table);
        if (!meta)
        {
          log(`no table meta for ${this.table} - ignoring event.`);
          return
        }
        if (!meta.options.index) {
          log(`no index for table ${this.table} - ignoring event.`);
          return
        }
        if (!meta.options.index) {
          log(`evt does not contain index colum ${meta.options.index}`, evt);
          notificationService.warning(`Perspective click event does not contain index colum ${meta.options.index}, so selection will not be tracked.`)
          return
        }
        const config = {
          filter: [[meta.options.index, "==", evt.detail.row[meta.options.index]]]
        };
        log('selected change', config)
        await pdqDataBroker.getOrCreateView(`${this.id}-selected-view`, this.table, config);
      })
      this._perspectiveAvailiable.resolve(true)
    }
  }
  syncConfigToPerspective = async () => {
    log('syncConfigToPerspective')
    try {
      this.config = JSON.parse(this.configEditor?.value || "");
      if (this.config) {
        this.perspectiveViewer?.restore(this.config);
        await this._updatePreview()
      }
    }catch(e){
      log(`Failed to parse config: ${e}`)
      notificationService.warning(`pdq-view#${this.id}|Error parsing JSON: \n${e?.message || e}`)
    }
  }
  syncConfigFromPerspective = async () => {
    log('syncConfigFromPerspective')
    await this._updatePreview()
  }
  render() {
    log(`pdq-data: ${this.id} ${this.show} - render`)
    const show = this.show|| pdqHotkeyService.showPdqData.currentValue();
    const slot = html`<slot id="pdq-perspective-viewer-slot-${this._id}"  @slotchange="${this._onSlotChange}"></slot>`
    const toolbar = html`
  <cds-icon-button slot="tool-bar" @click="${this.syncConfigFromPerspective}" kind="primary" >
    <span slot="tooltip-content"> Sync config from perspective </span>
    ${Upload({slot: 'icon'})}
  </cds-icon-button>
  <cds-icon-button slot="tool-bar" @click="${this.syncConfigToPerspective}" kind="primary">
    <span slot="tooltip-content"> Sync config to perspective </span>
    ${Download({slot: 'icon'})}
  </cds-icon-button>
`
    return html`
      ${slot}
      ${show ? html`
      <div class="show col ${this.error ? 'error' : ''} m-1">
        ${this.renderToolBar(toolbar)}
        <cds-textarea value="${JSON.stringify(this.config, null, 2)}">
          <label slot="label-text">Config</label>
        </cds-textarea>
      </div>` : nothing}

      <perspective-viewer
        theme="Pro Light"
        style="height: ${this.height}; --column-drop-label--display: block"
        ?selectable="${this.selectable}"
        id="pdq-perspective-viewer-${this.id || this._id}">
      </perspective-viewer>
    `;
  }
  protected async updateData() {
    log('updateData')
    if (this.#table) {
      log(`awaiting table: ${this.#table}`)
      const table = await pdqDataBroker.getTable(this.#table)
      log(`acquired table: ${this.#table}`)

      await this._perspectiveAvailiable.promise;
      log('loading table')
      this.perspectiveViewer?.load(table);
      if (!this._loaded.complete) {
        {
          log('first load');
          if (this.config !== PRE_INIT) {
            log('restoring config', this.config)
            await this.perspectiveViewer!.restore(this.config)
          }
          if (this.id) {
            //this.selectedView = await pdqDataBroker.getOrCreateView(`${this.id}-selected-view`, this.table)
            await pdqDataBroker.getDependentTable(`${this.id}-selected-table`, this.table, `${this.id}-selected-view`);
          }else{
            log(`no id to track selection table for ${this.id}`)
          }

          this._loaded.complete = true;
          this._loaded.resolve(true);
        }
        await this._updatePreview()
      }
    } else {
      log('updateData: no table to load...')
    }
  }
  private async _onSlotChange(event: Event) {
    log(`${this.id} - slot change`)
    const slot = event.target as HTMLSlotElement;
    const nodes = slot.assignedNodes({flatten: true});
    const {template, scripts} = extractTextAndScripts(nodes);
    if (!template.trim()){
      log(`ignoring blank config`)
      return
    }
    log(template)
    try {
      this.config = JSON.parse(template);

      if (this._loaded.complete) {
        log(`new config: `, this.config)
        await this.perspectiveViewer?.restore(this.config);
        await this._updatePreview()
      }else{
        log(`config not loaded due to table not being loaded yet.`);
      }
    }catch(e:any){
      log(`WARNING - pdq-view#${this.id}|Error parsing JSON: \n${e?.message || e}`)
      notificationService.warning(`pdq-view#${this.id}|Error parsing JSON: \n${e?.message || e}`)
    }

  }

  private async _updatePreview() {
    if (!this._loaded.complete)
      return
    log('update preview')
    this.config = await this.perspectiveViewer?.save();
    this.dataPreview = `<pdq-perspective-viewer${this.id ? html` id="${this.id}"` : ''}${this.table ? html` table="${this.table}"` : ''}><template>${
      JSON.stringify(this.config, null, 2)}</template></pdq-perspective-viewer>`;
  }
}
